import Loadable from 'components/Loadable';
import ProtectedRoute from 'components/ProtectedRoute';
import { APP_DEFAULT_PATH } from 'config';
import MainLayout from 'layout/MainLayout';
import { lazy } from 'react';
import { Navigate, Route } from 'react-router';
import { MerchantSection, Role } from 'types';
import CollectionDetails from './delivery/CollectionDetails';
import ReversmentDetails from './delivery/ReversmentDetails';

const AppHomepage = Loadable(lazy(() => import('pages/main/inApp/HomePage')));
const AppSettingsHomepage = Loadable(lazy(() => import('pages/main/inApp/settings')));
const AppSettingsAddStaff = Loadable(lazy(() => import('pages/main/inApp/settings/staff/addStaff')));
const AppSettingsStaffList = Loadable(lazy(() => import('pages/main/inApp/settings/staff/StaffList')));
const ProfilePage = Loadable(lazy(() => import('pages/main/inApp/profile')));
const ChangePassword = Loadable(lazy(() => import('pages/main/inApp/profile/ChangePassword')));
const AppSettingsStaffDetails = Loadable(lazy(() => import('pages/main/inApp/settings/staff/details')));
const SwitchEnrollmentOtp = Loadable(lazy(() => import('pages/main/inApp/settings/switch/enrollmentOtp')));
const AccountPage = Loadable(lazy(() => import('pages/main/inApp/account/AccountDetails')));
const CashInPage = Loadable(lazy(() => import('pages/main/inApp/cashIn')));
const CashOutPage = Loadable(lazy(() => import('pages/main/inApp/cashOut')));

const AddDeliveryMember = Loadable(lazy(() => import('pages/main/inApp/delivery/addDeliveryMember')));
const DeliveryMembers = Loadable(lazy(() => import('pages/main/inApp/delivery/deliveryMembers')));
const DeliveryMemberDetails = Loadable(lazy(() => import('pages/main/inApp/delivery/details')));

const SendMoneyMethods = Loadable(lazy(() => import('pages/main/inApp/sendMoney')));
const SendMoneyToBank = Loadable(lazy(() => import('pages/main/inApp/sendMoney/bank')));
const SendMoneyToWallet = Loadable(lazy(() => import('pages/main/inApp/sendMoney/wallet')));
const SendMoneyToSkypay = Loadable(lazy(() => import('pages/main/inApp/sendMoney/skypay')));
const QrCodePage = Loadable(lazy(() => import('pages/main/inApp/qrcode')));
const ReportsPage = Loadable(lazy(() => import('pages/main/inApp/reports')));

const AddBeneficiaryDemandes = Loadable(lazy(() => import('pages/main/inApp/demandes/addBeneficiaries')));
const AddSkypayBeneficiaryDemandes = Loadable(lazy(() => import('pages/main/inApp/demandes/addBeneficiaries/skypay')));
const AddBankBeneficiaryDemandes = Loadable(lazy(() => import('pages/main/inApp/demandes/addBeneficiaries/bank')));
const AddWalletBeneficiaryDemandes = Loadable(lazy(() => import('pages/main/inApp/demandes/addBeneficiaries/wallet')));
const TransactionDemandes = Loadable(lazy(() => import('pages/main/inApp/demandes/transactions')));
const SkypayTransactionDemandes = Loadable(lazy(() => import('pages/main/inApp/demandes/transactions/skypay')));
const BankTransactionDemandes = Loadable(lazy(() => import('pages/main/inApp/demandes/transactions/bank')));
const WalletTransactionDemandes = Loadable(lazy(() => import('pages/main/inApp/demandes/transactions/wallet')));

const UserActiveAddBeneficiaryDemandes = Loadable(lazy(() => import('pages/main/inApp/userActiveDemandes/addBeneficiaries')));
const UserActiveAddSkypayBeneficiaryDemandes = Loadable(lazy(() => import('pages/main/inApp/userActiveDemandes/addBeneficiaries/skypay')));
const UserActiveAddBankBeneficiaryDemandes = Loadable(lazy(() => import('pages/main/inApp/userActiveDemandes/addBeneficiaries/bank')));
const UserActiveAddWalletBeneficiaryDemandes = Loadable(lazy(() => import('pages/main/inApp/userActiveDemandes/addBeneficiaries/wallet')));
const UserActiveTransactionDemandes = Loadable(lazy(() => import('pages/main/inApp/userActiveDemandes/transactions')));
const UserActiveSkypayTransactionDemandes = Loadable(lazy(() => import('pages/main/inApp/userActiveDemandes/transactions/skypay')));
const UserActiveBankTransactionDemandes = Loadable(lazy(() => import('pages/main/inApp/userActiveDemandes/transactions/bank')));
const UserActiveWalletTransactionDemandes = Loadable(lazy(() => import('pages/main/inApp/userActiveDemandes/transactions/wallet')));

const Analytics = Loadable(lazy(() => import('pages/main/inApp/analytics')));

const InAppRoutes = <Route path='/*' element={<MainLayout />}>
  <Route path='home/*' >
    <Route path='' element={<AppHomepage />} />
    <Route path='*' element={<Navigate replace to={APP_DEFAULT_PATH} />} />
  </Route>
  <Route path='cashIn/*' element={<ProtectedRoute roles={[Role.SUPER_ADMIN]}><CashInPage /></ProtectedRoute>} />
  <Route path='cashOut/*' element={<ProtectedRoute roles={[Role.SUPER_ADMIN]}><CashOutPage /></ProtectedRoute>} />
  <Route path='account/*' >
    <Route path='' element={<AccountPage />} />
    <Route path='*' element={<Navigate replace to={APP_DEFAULT_PATH} />} />
  </Route>
  <Route path='delivery/*' element={<ProtectedRoute roles={[Role.SUPER_ADMIN, Role.ADMIN]} merchantSection={MerchantSection.DELIVERY} />}>
    <Route path='addMember' element={<AddDeliveryMember />} />
    <Route path='' element={<DeliveryMembers />} />
    <Route path='details' element={<DeliveryMemberDetails />} />
    <Route path='collectionDetails' element={<CollectionDetails />} />
    <Route path='reversmentDetails' element={<ReversmentDetails />} />
    <Route path='*' element={<Navigate replace to={"/delivery"} />} />
  </Route>
  <Route path='reports/*' element={<ProtectedRoute roles={[Role.SUPER_ADMIN, Role.ADMIN]} />}>
    <Route path='' element={<ReportsPage />} />
    <Route path='*' element={<Navigate replace to={APP_DEFAULT_PATH} />} />
  </Route>
  <Route path='qrcode/*' >
    <Route path='' element={<QrCodePage />} />
    <Route path='*' element={<Navigate replace to={APP_DEFAULT_PATH} />} />
  </Route>
  <Route path='settings/*' element={<ProtectedRoute roles={[Role.SUPER_ADMIN, Role.ADMIN]} />}>
    <Route path='' element={<AppSettingsHomepage />} />
    <Route path='staff/*'>
      <Route path='addStaff' element={<AppSettingsAddStaff />} />
      <Route path='' element={<AppSettingsStaffList />} />
      <Route path='details' element={<AppSettingsStaffDetails />} />
      <Route path='*' element={<Navigate replace to={APP_DEFAULT_PATH} />} />
    </Route>
    <Route path='enrollSwitch' element={<ProtectedRoute roles={[Role.SUPER_ADMIN]}><SwitchEnrollmentOtp /></ProtectedRoute>} />
    <Route path='*' element={<Navigate replace to={APP_DEFAULT_PATH} />} />
  </Route>
  <Route path='analytics/*' element={<ProtectedRoute roles={[Role.SUPER_ADMIN, Role.ADMIN]} merchantSection={MerchantSection.ANALYTICS}/>}>
    <Route path='' element={<Analytics />} />
    <Route path='*' element={<Navigate replace to={APP_DEFAULT_PATH} />} />
  </Route>
  <Route path='profile/*'>
    <Route path='' element={<ProfilePage />} />
    <Route path='changePassword' element={<ChangePassword />} />
    <Route path='*' element={<Navigate replace to={APP_DEFAULT_PATH} />} />
  </Route>
  <Route path='sendMoney/*'>
    <Route path='' element={<SendMoneyMethods />} />
    <Route path='bank/*' element={<SendMoneyToBank />} />
    <Route path='wallet/*' element={<SendMoneyToWallet />} />
    <Route path='skypay/*' element={<SendMoneyToSkypay />} />
    <Route path='*' element={<Navigate replace to={"/sendMoney"} />} />
  </Route>
  <Route path="requests/beneficiaries/*" element={<ProtectedRoute roles={[Role.SUPER_ADMIN, Role.ADMIN]} />}>
    <Route path="" element={<AddBeneficiaryDemandes />} />
    <Route path="skypay/*" element={<AddSkypayBeneficiaryDemandes />} />
    <Route path="bank/*" element={<AddBankBeneficiaryDemandes />} />
    <Route path="wallet/*" element={<AddWalletBeneficiaryDemandes />} />
    <Route path="*" element={<Navigate replace to={"/requests/beneficiaries"} />} />
  </Route>
  <Route path="requests/transactions/*" element={<ProtectedRoute roles={[Role.SUPER_ADMIN]} />}>
    <Route path="" element={<TransactionDemandes />} />
    <Route path="skypay/*" element={<SkypayTransactionDemandes />} />
    <Route path="bank/*" element={<BankTransactionDemandes />} />
    <Route path="wallet/*" element={<WalletTransactionDemandes />} />
    <Route path="*" element={<Navigate replace to={"/requests/transactions"} />} />
  </Route>
  <Route path="myRequests/beneficiaries/*" element={<ProtectedRoute roles={[Role.SUPPORT]} />}>
    <Route path="" element={<UserActiveAddBeneficiaryDemandes />} />
    <Route path="skypay/*" element={<UserActiveAddSkypayBeneficiaryDemandes />} />
    <Route path="bank/*" element={<UserActiveAddBankBeneficiaryDemandes />} />
    <Route path="wallet/*" element={<UserActiveAddWalletBeneficiaryDemandes />} />
    <Route path="*" element={<Navigate replace to={"/myRequests/beneficiaries"} />} />
  </Route>
  <Route path="myRequests/transactions/*" element={<ProtectedRoute roles={[Role.SUPPORT, Role.ADMIN]} />}>
    <Route path="" element={<UserActiveTransactionDemandes />} />
    <Route path="skypay/*" element={<UserActiveSkypayTransactionDemandes />} />
    <Route path="bank/*" element={<UserActiveBankTransactionDemandes />} />
    <Route path="wallet/*" element={<UserActiveWalletTransactionDemandes />} />
    <Route path="*" element={<Navigate replace to={"/myRequests/transactions"} />} />
  </Route>
  <Route path='' element={<Navigate replace to={APP_DEFAULT_PATH} />} />
  <Route path='*' element={<Navigate replace to={APP_DEFAULT_PATH} />} />
</Route>

export default InAppRoutes;